import { useState } from "react";
import { MapPin, Mail, Phone, Clock } from "lucide-react";
import { Toaster, toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useFetch } from "@/lib/content";
import { useSEO } from "@/lib/seo";
import { Eyebrow, Reveal } from "@/components/site/Motion";

export default function Contact() {
  const { data: settings } = useFetch("/content/settings");
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [website, setWebsite] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useSEO({
    title: "Contact RenB Solutions — Mumbai HQ",
    description: "Talk to RenB Solutions. Office: Parinee Crescenzo, Bandra Kurla Complex, Mumbai. Email hello@renbsolutions.com.",
    path: "/contact",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (website) return;
    setSending(true);
    try {
      await api.post("/leads", { ...form, source: "contact", website });
      setSent(true);
      toast.success("Message sent. We'll be in touch within one business day.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="mx-auto max-w-[1440px] px-5 pb-24 pt-28 md:px-10 md:pt-40">
      <Toaster richColors position="top-center" />
      <section className="grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Eyebrow>Contact</Eyebrow>
          <h1 className="mt-8 font-serif text-5xl font-medium leading-[1.02] tracking-tight md:text-7xl">
            Start the conversation<span className="text-progress">.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-obsidian/70">
            Write to us about where your business is stuck — or book a structured discovery call and answer a
            few questions so the first meeting is already useful.
          </p>

          {sent ? (
            <div data-testid="contact-success" className="mt-12 border border-ash bg-white p-10">
              <p className="font-serif text-3xl font-medium">Message received.</p>
              <p className="mt-3 text-sm text-obsidian/60">
                Thank you — we reply within one business day. If it&apos;s urgent, call us directly.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="mt-12 grid gap-8 sm:grid-cols-2">
              <input
                type="text"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
              />
              <label className="flex flex-col gap-2">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-slategray">Name *</span>
                <input required data-testid="contact-name" className="field" value={form.name} onChange={set("name")} placeholder="Your full name" />
              </label>
              <label className="flex flex-col gap-2">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-slategray">Email *</span>
                <input required type="email" data-testid="contact-email" className="field" value={form.email} onChange={set("email")} placeholder="you@company.com" />
              </label>
              <label className="flex flex-col gap-2">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-slategray">Phone</span>
                <input data-testid="contact-phone" className="field" value={form.phone} onChange={set("phone")} placeholder="+91" />
              </label>
              <label className="flex flex-col gap-2">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-slategray">Company</span>
                <input data-testid="contact-company" className="field" value={form.company} onChange={set("company")} placeholder="Company name" />
              </label>
              <label className="flex flex-col gap-2 sm:col-span-2">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-slategray">Where does it hurt?</span>
                <textarea
                  data-testid="contact-message"
                  rows={5}
                  className="field resize-none"
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell us briefly what you'd like to fix, grow or change."
                />
              </label>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={sending}
                  className="bg-obsidian px-8 py-4 text-sm font-bold uppercase tracking-[0.14em] text-paper transition-colors duration-300 hover:bg-progress disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Send message"}
                </button>
              </div>
            </form>
          )}
        </div>

        <aside className="lg:col-span-4 lg:col-start-9">
          <Reveal className="border border-ash bg-obsidian p-8 text-paper md:p-10" once>
            <p className="text-[13px] font-bold uppercase tracking-[0.2em] text-paper/50">Head office</p>
            <div className="mt-6 space-y-6 text-sm leading-relaxed">
              <p className="flex gap-3"><MapPin size={16} className="mt-0.5 shrink-0 text-progress" />{settings?.address || "10th Floor, Parinee Crescenzo, Bandra – Kurla Complex, Bandra East, Mumbai – 400051"}</p>
              <p className="flex gap-3"><Mail size={16} className="mt-0.5 shrink-0 text-progress" /><a href={`mailto:${settings?.contact_email || "hello@renbsolutions.com"}`} className="hover:text-progress">{settings?.contact_email || "hello@renbsolutions.com"}</a></p>
              <p className="flex gap-3"><Phone size={16} className="mt-0.5 shrink-0 text-progress" /><a href={`tel:${(settings?.phone || "+919920820106").replace(/\s/g, "")}`} className="hover:text-progress">{settings?.phone || "+91 99208 20106"}</a></p>
              <p className="flex gap-3"><Clock size={16} className="mt-0.5 shrink-0 text-progress" />Monday – Friday, 10:00 – 18:30 IST</p>
            </div>
            <div className="mt-10 border-t border-paper/10 pt-6">
              <a href="/book" data-testid="contact-book-link" className="inline-flex items-center gap-2 bg-progress px-6 py-3 text-[12px] font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-progress-dark">
                Book a Discovery Call
              </a>
            </div>
          </Reveal>
        </aside>
      </section>
    </div>
  );
}
